import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Hotkey } from '@tribeplatform/react-ui-kit/Hotkey'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T } from '../../i18n/index.js'
import { ChatAttachment } from '../../Messaging/types.js'
import { useComposer } from '../hooks/useComposer.js'

import { ChatAttachmentPickerButton } from './ChatAttachmentPickerButton.js'
import { ComposerIconButton } from './ComposerIconButton.js'
import { EmojiPickerButton } from './EmojiPickerButton.js'
import { MessagingCustomHotkey } from './MessagingCustomHotkey.js'
import { TextFormattingMenu } from './TextBubbleMenu.js'

export interface Props {
  disabledSubmit: boolean
  disableControls: boolean
  isLoading: boolean
  isEditingMessage?: boolean
  onCancelEdit?: () => void
  onSubmit: () => void
  onFileSelected?: (files: File[], type: ChatAttachment['type']) => void
}
export const ChatComposerControls = (props: Props) => {
  const {
    onFileSelected,
    disabledSubmit,
    disableControls,
    isLoading,
    onSubmit,
    isEditingMessage,
    onCancelEdit,
  } = props
  const { editor, toggleDialog, state } = useComposer()
  const { data: authMember } = useAuthMember()
  const { quickEnter } = authMember?.settings?.privateMessaging || {}

  if (!editor) {
    return null
  }

  return (
    <div className="space-y-1">
      {editor && state.textFormatMenu && (
        <TextFormattingMenu
          editor={editor}
          className="shadow-none border-none px-0 overflow-x-auto"
        />
      )}
      <div className="flex gap-1 items-center justify-start">
        <EmojiPickerButton
          size="md"
          editor={editor}
          disabled={disableControls}
        />
        <ComposerIconButton
          size="md"
          icon={<SvgIcon name="gif" />}
          disabled={disableControls}
          aria-label="Giphy"
          onClick={() => {
            toggleDialog('giphy', true)
          }}
        />
        <ChatAttachmentPickerButton
          size="md"
          type="image"
          disabled={disableControls}
          onFileSelected={onFileSelected}
        />
        <ChatAttachmentPickerButton
          size="md"
          onFileSelected={onFileSelected}
          type="file"
          disabled={disableControls}
        />
        <div className="flex items-center gap-x-2 grow justify-end">
          <Text
            variant="bodySm"
            className="text-xs text-content-subdued hidden md:block opacity-70"
          >
            {quickEnter ? (
              <T
                id="Messaging.Chat.Composer.ReturnForNewLine"
                defaultMessage="Press <shift_key>Shift</shift_key> <enter_key>Enter</enter_key> to add a new line"
                values={{
                  shift_key: ([value]) => (
                    <Hotkey
                      className="bg-surface-subdued px-1 rounded-base-sm"
                      size="sm"
                    >
                      {value}
                    </Hotkey>
                  ),
                  enter_key: ([value]) => (
                    <MessagingCustomHotkey value={value} />
                  ),
                }}
              />
            ) : (
              <T
                id="Messaging.Chat.Composer.ReturnToSend"
                defaultMessage="Press <cmd_key>command,ctrl</cmd_key> <enter_key>Enter</enter_key> to send"
                values={{
                  cmd_key: ([value]) => (
                    <Hotkey
                      className="bg-surface-subdued !text-sm px-1 rounded-base-sm"
                      size="sm"
                    >
                      {value}
                    </Hotkey>
                  ),
                  enter_key: ([value]) => (
                    <MessagingCustomHotkey value={value} />
                  ),
                }}
              />
            )}
          </Text>
          <div className="flex items-center gap-x-2">
            {isEditingMessage && (
              <IconButton
                loading={isLoading}
                size="lg"
                variant="secondaryNeutral"
                className="shrink-0"
                disabled={disableControls}
                onClick={onCancelEdit}
                icon={
                  <SvgIcon
                    className="size-4 transform rtl:-scale-x-100"
                    name="close"
                  />
                }
              />
            )}
            <IconButton
              loading={isLoading}
              size="lg"
              variant="primary"
              className="shrink-0"
              disabled={disabledSubmit || disableControls}
              onClick={onSubmit}
              icon={
                <SvgIcon
                  className="size-4 transform rtl:-scale-x-100"
                  name={isEditingMessage ? 'check' : 'send'}
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}
